import { getFlattenedNodes, SummaryType } from '@ls/grouping-service';
import { createSelector } from "reselect";
import { uniqueByKey, memoize } from '@ls/js-utils';
import CalcType from 'state/Constants/CalcType';
import ScaleType from 'state/Constants/ScaleType';
import SelectionState from "state/selections/SelectionState";
import { selectSelectedAccountEntityId, selectSelectedBenchmarkEntityId, selectSelectedAccountEntity } from 'state/selections/selectors';
import { getEntitySummariesObject, getEntitySummariesObjectForBench } from 'state/util';
import scope from "./scope";

export const selectData = (summaryType) => state => state[scope]['data'][summaryType];
export const selectFetchingData = (summaryType) => state => state[scope]['fetchingData'][summaryType];
export const selectFetchingTypes = state => state[scope].fetchingTypes;
export const selectTypes = state => state[scope].types;
export const selectFetchingPeriods = state => state[scope].fetchingPeriods;
export const selectPeriods = state => state[scope].periods;

export const selectDefinitions = memoize((selectionState) => {
  if (selectionState === SelectionState.UNCOMMITTED) {
    return state => state[scope]['definitions'][selectionState];
  }
  return createSelector(
    state => state[scope]['definitions'][selectionState],
    (definitions) => {
      const completeDefinitions = definitions.filter(({ complete }) => complete);
      return completeDefinitions.filter(uniqueByKey(completeDefinitions, 'key'));
    }
  );
});

export const selectRowMap = createSelector(
  selectDefinitions(SelectionState.COMMITTED),
  selectData(SummaryType.ABSOLUTE),
  selectData(SummaryType.RELATIVE),
  selectSelectedAccountEntityId(SelectionState.COMMITTED),
  selectSelectedBenchmarkEntityId(SelectionState.COMMITTED),
  selectSelectedAccountEntity(SelectionState.COMMITTED),
  (definitions, absData, relData, accEntityId, bmkEntityId, benchId) => {
    const absoluteNodes = getFlattenedNodes(absData?.root) ?? [];
    const relativeNodes = getFlattenedNodes(relData?.root) ?? [];
    return [...absoluteNodes, ...relativeNodes].reduce((rowMap, node) => {
      const { pathString } = node;
      rowMap[pathString] = rowMap[pathString] ?? { pathString };
      const row = rowMap[pathString];
      definitions
        .filter(({ key }) => !row[key])
        .forEach((definition) => {
          row[definition.key] = findFaaValue(
            node,
            definition,
            accEntityId,
            bmkEntityId,
            benchId?.benchmarkId,
          );
        });
      return rowMap;
    }, {});
  },
);

const camelize = (text) => {
  const a = text.toLowerCase()
    .replace(/[-_\s.]+(.)?/g, (_, c) => c ? c.toUpperCase() : '');
  return a.substring(0, 1).toLowerCase() + a.substring(1);
}

export const findFaaValue = (node, definition, accEntityId, bmkEntityId, benchId) => {
  const { factorType, calcType, period, scaleType } = definition;
  const entityId = calcType === CalcType.Benchmark ? bmkEntityId : accEntityId;
  const benchValue = calcType === CalcType.Benchmark ? 0 : benchId;
  const isRelative = calcType === CalcType.Relative;
  let entitySummariesObject;
  if (benchValue !== null && benchValue !== undefined) {
    entitySummariesObject = getEntitySummariesObjectForBench(node, entityId, benchValue, isRelative);
  } else {
    entitySummariesObject = getEntitySummariesObject(node, entityId, isRelative) ?? {};
  }
  const slaSummaryObject = entitySummariesObject?.['slaSummaries']?.find(({ primaryKey }) => primaryKey.period === period && primaryKey.entityId === entityId);
  let fatValueMap;
  switch (scaleType) {
    case ScaleType.Contribution:
      fatValueMap = slaSummaryObject?.['valuesContrib'] ?? []
      break;
    case ScaleType.WeightedAverage:
      fatValueMap = slaSummaryObject?.['valuesReturns'] ?? []
      break;
    default:
      throw new Error(`Unknown scaleType ${scaleType}`);
  }

  /*
    console.log('*****************************************');
    console.log('node', node);
    console.log('slaSummaryObject', slaSummaryObject);
    console.log('definition', definition);
    console.log('fatValueMap', fatValueMap);
  */

  return fatValueMap[camelize(factorType)];
};